import React, {useState, useCallback, useEffect} from 'react';
import '../../constants/styles/summaryPanel.scss';
import ChatTranscript from './summaryComponents/ChatTranscript';
import SummaryTranscript from './summaryComponents/SummaryTranscript';
import "../../constants/styles/loading.scss";

function SummaryPanel({ summaryData, conversationHistory, conversationId, updateSummaryData, setActivePanel }) {
    const [confirmationState, setConfirmationState] = useState('pending');
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [summarySent, setSummarySent] = useState(false);
    const [isSavingChanges, setIsSavingChanges] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [refreshChatTranscript, setRefreshChatTranscript] = useState(false);
    const [editBackground, setEditBackground] = useState(false); // State to manage background change

    const handleSaveChangesClick = useCallback(async () => {
        setIsSavingChanges(true);
        setConfirmationState('loading');
        setRefreshChatTranscript(true);
        setEditBackground(false); // Revert background color on click

        setTimeout(async () => {
            const requestBody = { ID: conversationId };
            const response = await fetch("https://www.cs.toronto.edu/~brli/intakebot/api/summaryEdited", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                updateSummaryData(data.result);
                setIsEditing(false);
                setConfirmationState('pending');
                setRefreshChatTranscript(false);
            } else {
                console.error('Error saving changes');
                setConfirmationState('error');
                setRefreshChatTranscript(false);
            }
            setIsSavingChanges(false);
        }, 0);
    }, [conversationId, updateSummaryData, setIsEditing]);

    const closeDialog = () => {
        setShowConfirmationDialog(false);
    };

    const handleSendSummary = () => {
        setSummarySent(true);
    };

    const handleNoConfirm = () => {
        setShowConfirmationDialog(true);
    };

    const handleContinue = () => {
        setActivePanel('PostSession');
    };

    useEffect(() => {
        setEditBackground(isEditing); // Change background when isEditing changes
    }, [isEditing]);

    return (
        <div className="SummaryPanelContainer">
            {!showConfirmationDialog && (
                <div className="TitleContainer">
                    <h1 className="Title">Chat</h1>
                    <h1 className="Title">
                        Summary
                        {isEditing && (
                            <button className={`saveChangesButton ${isSavingChanges ? 'disabled' : ''}`}
                                    onClick={handleSaveChangesClick} disabled={isSavingChanges}>
                                Update
                            </button>
                        )}
                    </h1>
                </div>
            )}
            {!showConfirmationDialog ? (
                <div className="ContentContainer">
                    <div className={`TranscriptPanel LeftPanel ${confirmationState === 'finalConfirm' || confirmationState === 'sent' ? 'TransparentBackground' : ''}`}>
                        <ChatTranscript
                            conversationHistory={conversationHistory}
                            refreshTranscript={refreshChatTranscript}
                            onSummaryConfirmationNeeded={setConfirmationState}
                            conversationId={conversationId}
                            updateSummaryData={updateSummaryData}
                            onNoConfirm={handleNoConfirm}
                            setIsEditing={setIsEditing}
                            isSavingChanges={isSavingChanges}
                        />
                    </div>
                    <div className={`TranscriptPanel RightPanel ${editBackground ? 'editBackground' : ''}`}>
                        {confirmationState === 'loading' ? (
                            <div className="loadingScreen">
                                <div className="loading-spinner"></div>
                                <p>Generating summary...</p>
                            </div>
                        ) : (
                            <SummaryTranscript summaryData={summaryData} isEditing={isEditing} />
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="ConfirmationPanel">
                        {!summarySent ? (
                            <>
                                <p>Are you ready to send your summary to the clinician?</p>
                                <div className="button-container">
                                    <button className="yesButton" onClick={handleSendSummary}>Yes</button>
                                    <button className="noButton" onClick={closeDialog}>No</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="confirmationMessage">The summary is sent to your clinician!</p>
                                <hr className="confirmationDivider" />
                                <p>Please press "Continue" to move forward to the final survey of this study.</p>
                            </>
                        )}
                    </div>
                    {summarySent && <button className="continueButton" onClick={handleContinue}>Continue</button>}
                </>
            )}
        </div>
    );
}

export default SummaryPanel;
